import { Container } from "@mui/material";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Turn as Hamburger } from "hamburger-react";
import { useState, useRef, useEffect, useCallback } from "react";
import ChairIcon from '@mui/icons-material/Chair';
import InventoryIcon from '@mui/icons-material/Inventory';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ComputerIcon from '@mui/icons-material/Computer';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import ContactsIcon from '@mui/icons-material/Contacts';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

import "./navbar.scss";

const PRODUCT_CATEGORIES = [
  {
    path: "/products",
    label: "All Products",
    icon: <InventoryIcon />,
    desc: "Browse our complete catalog of rentals",
  },
  {
    path: "/furniture-packages",
    label: "Furniture Packages",
    icon: <ChairIcon />,
    desc: "Sofas, beds, tables, chairs & wardrobes",
  },
  {
    path: "/electronic-appliances",
    label: "Electronic Appliances",
    icon: <AcUnitIcon />,
    desc: "ACs, washing machines, refrigerators & more",
  },
  {
    path: "/computer-peripherals",
    label: "Computer Peripherals",
    icon: <ComputerIcon />,
    desc: "Laptops, monitors, printers & accessories",
  },
  {
    path: "/recreational-setups",
    label: "Recreational Setups",
    icon: <SportsEsportsIcon />,
    desc: "Gaming zones, lounge & entertainment setups",
  },
  {
    path: "/commercial-fitness",
    label: "Commercial Fitness",
    icon: <FitnessCenterIcon />,
    desc: "Treadmills, gym equipment & wellness gear",
  },
  {
    path: "/food-and-beverages",
    label: "Food & Beverages",
    icon: <RestaurantIcon />,
    desc: "Cloud kitchen, café & restaurant equipment",
  },
];

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const megaMenuRef = useRef(null);
  const navigate = useNavigate();

  const closeMobileMenu = useCallback(() => {
    setMobileOpen(false);
    setMobileProductsOpen(false);
  }, []);

  // Close mega menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target)) {
        setMegaMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleProductNav = (path) => {
    navigate(path);
    closeMobileMenu();
    setMegaMenuOpen(false);
  };

  return (
    <header className="header" role="banner">
      <Container maxWidth="lg">
        <nav aria-label="Main navigation">
          {/* ── Desktop Left ── */}
          <div className="nav-left">
            <Link to="/" className="logo" aria-label="Settlrs Home">
              <img
                src="https://d5r8zwzwkh326.cloudfront.net/new+ui+images/settlrs-transparent-logo.png"
                alt="Settlrs logo"
                width="140"
                height="40"
              />
            </Link>

            <div className="nav-links">
              <NavLink
                exact="true"
                to="/"
                activeclassname="active"
                className="nav-link"
              >
                Home
              </NavLink>

              {/* Products dropdown */}
              <div
                className={`nav-products-wrapper${megaMenuOpen ? " open" : ""}`}
                ref={megaMenuRef}
                onMouseEnter={() => setMegaMenuOpen(true)}
                onMouseLeave={() => setMegaMenuOpen(false)}
              >
                <button
                  className="nav-link nav-products-btn"
                  aria-haspopup="true"
                  aria-expanded={megaMenuOpen}
                  onClick={() => setMegaMenuOpen((v) => !v)}
                >
                  Products
                  <svg
                    className={`chevron${megaMenuOpen ? " up" : ""}`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 4l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {/* Mega Menu */}
                <div
                  className={`mega-menu${megaMenuOpen ? " visible" : ""}`}
                  role="menu"
                  aria-label="Product categories"
                >
                  <div className="mega-menu-inner">
                    <p className="mega-menu-heading">Browse by Category</p>
                    <div className="mega-menu-grid">
                      {PRODUCT_CATEGORIES.map((cat) => (
                        <button
                          key={cat.path}
                          className="mega-menu-item"
                          role="menuitem"
                          onClick={() => handleProductNav(cat.path)}
                        >
                          <span className="mega-menu-icon" aria-hidden="true">
                            {cat.icon}
                          </span>
                          <span className="mega-menu-text">
                            <span className="mega-menu-label">{cat.label}</span>
                            <span className="mega-menu-desc">{cat.desc}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="mega-menu-cta">
                      <button
                        className="mega-cta-btn"
                        onClick={() => {
                          navigate("/contact-us");
                          setMegaMenuOpen(false);
                        }}
                      >
                        Get a Custom Quote →
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <NavLink
                to="/contact-us"
                activeclassname="active"
                className="nav-link"
              >
                Contact Us
              </NavLink>
              <NavLink
                to="/about-us"
                activeclassname="active"
                className="nav-link"
              >
                About Us
              </NavLink>
            </div>
          </div>

          {/* ── Desktop Right CTA ── */}
          <div className="nav-right">
            <button
              className="nav-cta-btn"
              onClick={() => navigate("/contact-us")}
              aria-label="Get Started with Settlrs"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Hamburger */}
          <div className="nav-mobile-toggle" aria-label="Toggle mobile menu">
            <Hamburger
              toggled={mobileOpen}
              toggle={setMobileOpen}
              size={20}
              label="Toggle menu"
            />
          </div>
        </nav>
      </Container>

      {/* Mobile Drawer */}
      <div
        className={`mobile-drawer${mobileOpen ? " open" : ""}`}
        aria-hidden={!mobileOpen}
      >
        <div className="mobile-drawer-inner">
          <nav aria-label="Mobile navigation">
            <a href="/" className="mobile-nav-link" onClick={closeMobileMenu}>
              <span className="mobile-link-inner">
                <HomeIcon /> Home
              </span>
            </a>

            {/* Mobile Products accordion */}
            <div className="mobile-products-accordion">
              <button
                className="mobile-nav-link mobile-accordion-trigger"
                onClick={() => setMobileProductsOpen((v) => !v)}
                aria-expanded={mobileProductsOpen}
              >
                <span className="mobile-link-inner">
                  <InventoryIcon /> Products
                </span>
                <svg
                  className={`chevron${mobileProductsOpen ? " up" : ""}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 4l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {mobileProductsOpen && (
                <div className="mobile-products-list">
                  {PRODUCT_CATEGORIES.map((cat) => (
                    <button
                      key={cat.path}
                      className="mobile-product-item"
                      onClick={() => handleProductNav(cat.path)}
                    >
                      <span aria-hidden="true">{cat.icon}</span> {cat.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="/contact-us"
              className="mobile-nav-link"
              onClick={closeMobileMenu}
            >
              <span className="mobile-link-inner">
                <ContactsIcon /> Contact Us
              </span>
            </a>
            <a
              href="/about-us"
              className="mobile-nav-link"
              onClick={closeMobileMenu}
            >
              <span className="mobile-link-inner">
                <InfoIcon /> About Us
              </span>
            </a>
            <a
              href="/case-studies"
              className="mobile-nav-link"
              onClick={closeMobileMenu}
            >
              <span className="mobile-link-inner">
                <LibraryBooksIcon /> Case Studies
              </span>
            </a>
          </nav>

          <button
            className="mobile-cta-btn"
            onClick={() => {
              navigate("/contact-us");
              closeMobileMenu();
            }}
          >
            Get Started →
          </button>
        </div>
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="mobile-backdrop"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
    </header>
  );
}

export default Navbar;
